import { combineEpics, Epic } from 'redux-observable';
import { from, of, EMPTY } from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import { isOfType } from 'typesafe-actions';
import { Position, Toaster, Intent } from '@blueprintjs/core';

import {
  ReportActions,
  ReportActionTypes,
  loadingReports,
  loadedReports,
  loadingReportsFailed,
  createdReport,
  creatingReport,
  creatingReportFailed,
  deletedReport,
  deletingReport,
  deletingReportFailed,
  duplicatedReport,
  duplicatingReport,
  duplicatingReportFailed,
  updatedReports,
  updatingReports,
  updatingReportsFailed,
  bulkActionSuccess,
} from '../actions/reportActions';
import {
  ReportFormActions,
  ReportFormActionTypes,
  loadingReport,
  loadedReport,
  loadingReportFailed,
  updatedReport,
  updatingReport,
  updatingReportFailed,
} from '../actions/reportFormActions';
import { IState } from '../reducers';
import {
  getReport,
  getReports,
  createReport,
  updateReport,
  updateReports,
  deleteReport,
  deleteReports,
  duplicateReport,
} from '../../services/reportApi';

export const reportToaster = Toaster.create({
  className: 'report-toaster',
  position: Position.TOP
});

const loadReportEpic: Epic<ReportFormActions, ReportFormActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportFormActionTypes.LOAD_REPORT)),
    switchMap((action: any) =>
      from(getReport(action.payload)).pipe(
        map((response: any) => loadedReport(response.result)),
        startWith(loadingReport()),
        catchError((error: any) => {
          return of(loadingReportFailed());
        })
      )
    )
  );

const loadReportsEpic: Epic<ReportActions, ReportActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.LOAD_REPORTS)),
    switchMap((action: any) =>
      from(getReports({
          filters: action.payload.filters,
          page: action.payload.page,
          page_size: action.payload.page_size
        }, action.payload.sort)).pipe(
        map((response: any) => loadedReports(
          response.result,
          action.payload.reload,
          action.payload.page_size,
        )),
        startWith(loadingReports()),
        catchError((error: any) => {
          return of(loadingReportsFailed());
        })
      )
    )
  );

const createReportEpic: Epic<ReportActions, ReportActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.CREATE_REPORT)),
    switchMap((action: any) =>
      from(createReport(action.payload)).pipe(
        map((response: any) => createdReport(response.result)),
        startWith(creatingReport()),
        catchError((error: any) => {
          let message = error.response?.data?.message || "";

          if (typeof message === 'object') {
            /*
             * {field: [error], ...}
             */
            const key = Object.keys(message)[0];
            const keyError = message[key][0];

            message = `Field '${key}': ${keyError}`;
          }

          reportToaster.show({
            message: `An error occurred while creating the report: ${message}`,
            intent: Intent.DANGER,
          });

          return of(creatingReportFailed());
        })
      )
    )
  );

const updateReportEpic: Epic<ReportFormActions, ReportFormActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(ReportFormActionTypes.UPDATE_REPORT)),
    switchMap((action: any) =>
      from(updateReport(action.payload.uid as string, action.payload.report)).pipe(
        map((response: any) => updatedReport({
          ...response.result,
          uid: action.payload.uid
        })),
        startWith(updatingReport()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          reportToaster.show({
            message: `An error occurred while updating: ${message}.`,
            intent: Intent.DANGER
          });

          return of(updatingReportFailed());
        })
      )
    )
  );

const updateReportsEpic: Epic<ReportFormActions, ReportFormActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.UPDATE_REPORTS)),
    switchMap((action: any) =>
      from(updateReports(action.payload.reports)).pipe(
        map((response: any) => updatedReports(response.result)),
        startWith(updatingReports()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          reportToaster.show({
            message: `An error occurred while updating reports: ${message}.`,
            intent: Intent.DANGER
          });

          return of(updatingReportsFailed());
        })
      )
    )
  );

const deleteReportEpic: Epic<ReportActions, ReportActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.DELETE_REPORT)),
    switchMap((action: any) =>
      from(deleteReport(action.payload)).pipe(
        map((response: any) => {
          if (response?.message === 'OK') {
            reportToaster.show({
              message: `Report successfully deleted.`,
              intent: Intent.SUCCESS
            });
          }
          return deletedReport(action.payload);
        }),
        startWith(deletingReport()),
        catchError((error: any) => {
          const message = error.response?.data?.message || "";

          reportToaster.show({
            message: `An error occurred while deleting report: ${message}.`,
            intent: Intent.DANGER
          });

          return of(deletingReportFailed());
        })
      )
    )
  );

const duplicateReportEpic: Epic<ReportActions, ReportActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.DUPLICATE_REPORT)),
    switchMap((action: any) =>
      from(duplicateReport(action.payload)).pipe(
        map((response: any) => duplicatedReport(response.result)),
        startWith(duplicatingReport()),
        catchError((error: any) => {
          let message = error.response?.data?.message || "";

          reportToaster.show({
            message: `An error occurred while duplicating report: ${message}`,
            intent: Intent.DANGER,
          });

          return of(duplicatingReportFailed());
        })
      )
    )
  );

const bulkActionEpic: Epic<ReportActions, ReportActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(ReportActionTypes.BULK_ACTION)),
    switchMap((action: any) => {
      const {action: bulkAction, selected} = action.payload;

      let bulkActionRequest = null;
      switch (bulkAction) {
        case 'delete':
          bulkActionRequest = deleteReports(selected);
          break;
      }

      if (!bulkActionRequest) {
        return EMPTY;
      }

      return from(bulkActionRequest).pipe(
        map((response: any) => bulkActionSuccess(bulkAction, selected)),
        catchError((error: any) => {
          let message = error.response?.data?.message || "";

          reportToaster.show({
            message: `An error occurred: ${message}`,
            intent: Intent.DANGER,
          });

          return of({type: 'void'});
        })
      )
    })
  );

export default combineEpics(
  loadReportEpic,
  loadReportsEpic,
  createReportEpic,
  updateReportEpic,
  updateReportsEpic,
  deleteReportEpic,
  duplicateReportEpic,
  bulkActionEpic,
);
